import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Send, Landmark, Eye, FileSignature, FileDown, FileStack, FileText, Banknote, BadgeCheck, Stamp, CalendarDays } from 'lucide-react';
import { api } from '@/lib/api';

const RAIL_ENDPOINTS = { mint: '/equity/mint-transactions?limit=15', cyclos: '/equity/cyclos-transactions?limit=15' };

export const DoronPayLayer = () => {
  const [st, setSt] = useState(null);
  const [summary, setSummary] = useState(null);
  const [clearing, setClearing] = useState(null);
  const [vtAmount, setVtAmount] = useState('');
  const [vtRail, setVtRail] = useState('mint');
  const [vtAccount, setVtAccount] = useState('3919226411278897070');
  const [vtCyclosSource, setVtCyclosSource] = useState('master_to_member');
  const [vtResult, setVtResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [prepared, setPrepared] = useState(null);
  const [railTxns, setRailTxns] = useState(null);
  const [railLoading, setRailLoading] = useState(false);
  const [form, setForm] = useState({ amount: '100', description: 'EzyTransfer USD settlement', external_reference: '', source_rail: 'mint', source_rail_reference: '', _rail_details: null });
  const [evt, setEvt] = useState({ external_reference: '', status: 'SUBMITTED', transaction_id: '', amount: '100' });

  const load = async () => {
    try {
      const [h, l, sm] = await Promise.all([api.get('/doronpay/health'), api.get('/doronpay/instructions'), api.get('/doronpay/summary')]);
      setSt(h.data);
      setRows(l.data.instructions || []);
      setSummary(sm.data);
    } catch (e) { toast.error('DoronPay health failed'); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/doronpay/clearing-monitor').then((r) => setClearing(r.data)).catch(() => {});
  }, []);

  const payload = () => {
    const { _rail_details, ...rest } = form;
    return { ...rest, amount: parseFloat(form.amount), source_rail_details: _rail_details || null };
  };

  const prepare = async () => {
    if (!form.amount) { toast.error('Amount required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/doronpay/credit-instruction', { ...payload(), confirm: false });
      setPrepared(r.data.request);
      toast.success('Signed request prepared — nothing sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Prepare failed'); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    if (!prepared) { toast.error('Prepare first'); return; }
    if (!window.confirm(`Send USD ${form.amount} credit instruction to DoronPay LIVE?`)) return;
    setBusy(true);
    try {
      const r = await api.post('/doronpay/credit-instruction', { ...payload(), external_reference: prepared.reference, confirm: true });
      if (r.data.mode === 'executed') toast.success(`Instruction accepted — ${r.data.result?.doron_status || 'PENDING_SETTLEMENT'}`);
      else toast.error(`DoronPay rejected: ${JSON.stringify(r.data.result?.response).slice(0, 120)}`);
      setPrepared(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Execute failed'); }
    finally { setBusy(false); }
  };

  const openRailPicker = async () => {
    if (railTxns) { setRailTxns(null); return; }
    setRailLoading(true);
    try {
      const r = await api.get(RAIL_ENDPOINTS[form.source_rail] || RAIL_ENDPOINTS.mint);
      setRailTxns(r.data.transactions || []);
    } catch (e) { toast.error('Rail list failed'); }
    finally { setRailLoading(false); }
  };

  const pickRailTxn = (t) => {
    setForm((f) => ({ ...f, source_rail_reference: t.transaction_number || '', _rail_details: { amount: t.amount, date: t.date, description: t.description, party: t.party } }));
    setRailTxns(null);
    toast.success(`${form.source_rail.toUpperCase()} txn ${t.transaction_number} linked as funding leg`);
  };

  const sendEvent = async (confirm) => {
    if (!evt.external_reference) { toast.error('External reference required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/doronpay/settlement-event', { ...evt, amount: parseFloat(evt.amount), transaction_id: evt.transaction_id || null, confirm });
      if (!confirm) { setPrepared(r.data.request); toast.success('Event prepared'); }
      else if (r.data.mode === 'executed') { toast.success(`Event accepted (${evt.status})`); load(); }
      else toast.error(`Rejected: ${JSON.stringify(r.data.response).slice(0, 120)}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Event failed'); }
    finally { setBusy(false); }
  };

  const downloadReceipt = async (row) => {
    try {
      const r = await api.get(`/doronpay/instructions/${row.id}/receipt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EzyTransfer_DoronPay_Receipt_${row.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt PDF downloaded');
    } catch (e) { toast.error('Receipt download failed'); }
  };

  const downloadDemoPack = async () => {
    try {
      const r = await api.get('/doronpay/demo-pack', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'EzyTransfer_DoronPay_DemoPack.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('DoronPay demo pack PDF downloaded');
    } catch (e) { toast.error('Demo pack download failed'); }
  };

  const downloadEvidencePack = async (row) => {
    try {
      const r = await api.get(`/doronpay/instructions/${row.id}/evidence-pack`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EzyTransfer_DoronPay_EvidencePack_${row.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Evidence pack PDF downloaded');
    } catch (e) { toast.error('Evidence pack download failed'); }
  };

  const downloadPdf = async (path, filename, okMsg) => {
    try {
      const r = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(okMsg);
    } catch (e) { toast.error('PDF download failed'); }
  };

  const markRedeemed = async (row) => {
    const channel = window.prompt('Redemption channel: ledger_posting | bank_settlement | network_membership', 'ledger_posting');
    if (!channel) return;
    const reference = window.prompt('Redemption reference (posting ref / wire ref)', '') || '';
    if (!window.confirm(`Mark ${row.reference} as REDEEMED via ${channel}? This is one-way and blocks double redemption.`)) return;
    setBusy(true);
    try {
      await api.post(`/doronpay/instructions/${row.id}/redeem`, { channel, reference });
      toast.success(`${row.reference} marked REDEEMED via ${channel}`);
      if (channel === 'bank_settlement') {
        await downloadPdf(`/doronpay/instructions/${row.id}/redemption-receipt`, `EzyTransfer_DoronPay_RedemptionReceipt_${row.reference}.pdf`, 'Stamped redemption receipt downloaded');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Redeem failed'); }
    finally { setBusy(false); }
  };

  const runValueToken = async (confirm) => {
    const amt = parseFloat(vtAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    const railLabel = vtRail === 'cyclos' ? 'Cyclos ledger value units' : 'the Mint token account';
    if (confirm && !window.confirm(`EXECUTE LIVE value token transfer of ${amt.toLocaleString()} USD?\nThis moves ${railLabel} for real and issues a DoronPay instruction.`)) return;
    setBusy(true);
    try {
      const payload = vtRail === 'cyclos'
        ? { amount: amt, rail: 'cyclos', cyclos_source: vtCyclosSource, confirm }
        : { amount: amt, rail: 'mint', mint_account_id: vtAccount, confirm };
      const r = await api.post('/doronpay/value-token', payload);
      setVtResult(r.data);
      if (r.data.mode === 'pending_authorization') {
        toast.warning('Cyclos leg awaits dual-control authorization — authorize in Cyclos, then press Complete');
      } else {
        toast.success(confirm ? 'Value token transfer executed' : 'Value token prepared (no funds moved)');
      }
      if (confirm) load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Value token call failed'); }
    finally { setBusy(false); }
  };

  const completeValueToken = async (pendingId) => {
    setBusy(true);
    try {
      const r = await api.post('/doronpay/value-token/complete', { pending_id: pendingId });
      setVtResult(r.data);
      if (r.data.mode === 'executed') { toast.success(`${r.data.reference}: Cyclos authorized — DoronPay instruction issued`); load(); }
      else toast.warning(`Still pending authorization (${r.data.authorization_status || 'pending'})`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Complete failed'); }
    finally { setBusy(false); }
  };

  const pollRow = async (row) => {
    setBusy(true);
    try {
      const r = await api.post(`/doronpay/instructions/${row.id}/poll`);
      toast.success(`${row.reference}: ${r.data.doron_status || 'no change'}${r.data.spendable ? ' · SPENDABLE' : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Poll failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="doronpay-layer">
      <Card className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Landmark className="w-5 h-5 text-indigo-700" /> DoronPay — USD Ledger Settlement</h3>
            <p className="text-xs text-slate-500 mt-1">Unified API v1.2 · HMAC-SHA256 signed · asset USD · INTERNAL_LEDGER · Mint/Cyclos funding legs</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`border ${st?.configured ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`} data-testid="doronpay-configured-badge">
              {st?.configured ? 'CONFIGURED' : 'AWAITING CONFIG'}
            </Badge>
            <Button variant="outline" size="sm" onClick={() => downloadPdf('/doronpay/release-pack', 'EzyTransfer_DoronPay_ReleasePack_MA-USD000000000042.pdf', 'Release pack downloaded')} data-testid="doronpay-release-pack-btn">
              <FileDown className="w-4 h-4 mr-1" /> Release Pack
            </Button>
            <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => downloadPdf('/doronpay/recall/confirmation', 'EzyTransfer_DoronPay_RecallConfirmationPack.pdf', 'Recall confirmation pack downloaded')} data-testid="doronpay-recall-pack-btn">
              <FileDown className="w-4 h-4 mr-1" /> Recall Pack
            </Button>
            <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => downloadPdf('/doronpay/recall/notice', 'EzyTransfer_DoronPay_RecallNotice.pdf', 'Provider recall notice downloaded')} data-testid="doronpay-recall-notice-btn">
              <FileSignature className="w-4 h-4 mr-1" /> Provider Notice
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              const m = new Date().toISOString().slice(0, 7);
              downloadPdf(`/doronpay/monthly-statement?month=${m}`, `EzyTransfer_DoronPay_Statement_${m}.pdf`, 'Monthly statement downloaded');
            }} data-testid="doronpay-monthly-statement-btn">
              <CalendarDays className="w-4 h-4 mr-1" /> Monthly Statement
            </Button>
            <Button variant="outline" size="sm" onClick={downloadDemoPack} data-testid="doronpay-demo-pack-btn">
              <FileStack className="w-4 h-4 mr-1" /> Demo Pack (PDF)
            </Button>
            <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
        {st && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-[11px] font-mono text-slate-600" data-testid="doronpay-config-grid">
            <p>Endpoint: {st.base_url}</p>
            <p>Provider ID: {st.provider_id}</p>
            <p>Beneficiary: {st.beneficiary_reference}</p>
            <p>HMAC secret: {st.hmac_secret_set ? 'set ✓' : 'missing'}</p>
            <p className="md:col-span-2">Second listener: {st.second_listener_url || '—'}</p>
          </div>
        )}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="doronpay-summary-strip">
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2" data-testid="doronpay-summary-ringfenced">
              <p className="text-[10px] uppercase text-slate-500">Ring-fenced</p>
              <p className="text-lg font-semibold font-mono text-slate-800">${Number(summary.ring_fenced_usd).toFixed(2)}</p>
              <p className="text-[10px] text-slate-500">{summary.ring_fenced_count} instruction{summary.ring_fenced_count === 1 ? '' : 's'} · backing held on Cyclos</p>
            </div>
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="doronpay-summary-redeemed">
              <p className="text-[10px] uppercase text-emerald-700">Redeemed</p>
              <p className="text-lg font-semibold font-mono text-emerald-700">${Number(summary.redeemed_usd).toFixed(2)}</p>
              <p className="text-[10px] text-emerald-700/70">{summary.redeemed_count} instruction{summary.redeemed_count === 1 ? '' : 's'} · released</p>
            </div>
            <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2" data-testid="doronpay-summary-recalled">
              <p className="text-[10px] uppercase text-rose-700">Recalled</p>
              <p className="text-lg font-semibold font-mono text-rose-700">${Number(summary.recalled_usd || 0).toFixed(2)}</p>
              <p className="text-[10px] text-rose-700/70">{summary.recalled_count || 0} instruction{(summary.recalled_count || 0) === 1 ? '' : 's'} · value returned to Master</p>
            </div>
            <div className="rounded border border-indigo-200 bg-indigo-50 px-3 py-2" data-testid="doronpay-summary-total">
              <p className="text-[10px] uppercase text-indigo-700">Total instructed</p>
              <p className="text-lg font-semibold font-mono text-indigo-700">${Number(summary.total_usd).toFixed(2)}</p>
              <p className="text-[10px] text-indigo-700/70">all accepted instructions</p>
            </div>
          </div>
        )}
      </Card>

      {clearing && (
        <Card className="p-6" data-testid="doronpay-clearing-monitor">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Landmark className="w-4 h-4 text-indigo-600" /> Cyclos Clearing Monitor</h3>
              <p className="text-xs text-slate-500">Live from the Cyclos ledger · as of {String(clearing.as_of || '').slice(0, 19).replace('T', ' ')} UTC</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => api.get('/doronpay/clearing-monitor').then((r) => setClearing(r.data)).catch(() => {})} data-testid="doronpay-clearing-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2" data-testid="doronpay-clearing-balance">
              <p className="text-[10px] uppercase text-slate-500">{clearing.clearing?.name || 'Settlement Clearing Account'} · {clearing.clearing?.number}</p>
              <p className="text-lg font-semibold font-mono text-slate-800">${Number(clearing.clearing?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-slate-500">cumulative issuance (negative = total funded out)</p>
            </div>
            <div className="rounded border border-indigo-200 bg-indigo-50 px-3 py-2" data-testid="doronpay-master-balance">
              <p className="text-[10px] uppercase text-indigo-700">{clearing.master?.name || 'Global USD Master Account'}</p>
              <p className="text-lg font-semibold font-mono text-indigo-700">${Number(clearing.master?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-indigo-700/70">backing funds currently held for delivery</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-50"><tr><th className="text-left px-2 py-1">Date</th><th className="text-right px-2 py-1">Amount</th><th className="text-left px-2 py-1">Cyclos txn</th><th className="text-left px-2 py-1">Description</th></tr></thead>
              <tbody>
                {(clearing.recent || []).map((h, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`doronpay-clearing-row-${h.transaction_number}`}>
                    <td className="px-2 py-1 font-mono text-[10px]">{(h.date || '').replace('T', ' ')}</td>
                    <td className="px-2 py-1 text-right font-mono">{Number(h.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-2 py-1 font-mono text-[10px]">{h.transaction_number}</td>
                    <td className="px-2 py-1 text-slate-500">{h.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-6" data-testid="doronpay-value-token-card">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-1"><Banknote className="w-4 h-4 text-indigo-600" /> Value Token Transfer (Mint / Cyclos → DoronPay)</h3>
        <p className="text-xs text-slate-500 mb-3">Same pattern as the SOVEIN / ALT1 / TAP value sends: a real token/value-unit movement (USD Mint debit or Cyclos ledger transfer) funds the DoronPay instruction, with a Digital Token Receipt (DTR).</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase text-slate-500">Amount (USD)</label>
            <input type="number" min="0" step="0.01" value={vtAmount} onChange={(e) => setVtAmount(e.target.value)} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono" placeholder="0.00" data-testid="doronpay-vt-amount-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500">Value rail</label>
            <select value={vtRail} onChange={(e) => setVtRail(e.target.value)} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="doronpay-vt-rail-select">
              <option value="mint">USD Mint (token debit)</option>
              <option value="cyclos">Cyclos (ledger value units)</option>
            </select>
          </div>
          {vtRail === 'mint' ? (
            <div>
              <label className="text-[10px] uppercase text-slate-500">Mint token source account</label>
              <select value={vtAccount} onChange={(e) => setVtAccount(e.target.value)} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="doronpay-vt-account-select">
                <option value="3919226411278897070">EZYtransfer 63995800 (050419305)</option>
                <option value="3919223574897594286">EZ 63995800 (191858584)</option>
                <option value="3919223574826291118">Bridge 63995800 (306297409)</option>
              </select>
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase text-slate-500">Cyclos value route</label>
              <select value={vtCyclosSource} onChange={(e) => setVtCyclosSource(e.target.value)} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid="doronpay-vt-cyclos-source-select">
                <option value="master_to_member">Master → DoronPay IGO account (4408246838737897)</option>
                <option value="clearing_to_master">Clearing → Master (backing leg)</option>
              </select>
            </div>
          )}
          <div className="flex items-end gap-2">
            <Button variant="outline" size="sm" onClick={() => runValueToken(false)} disabled={busy} data-testid="doronpay-vt-prepare-btn">Prepare</Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => runValueToken(true)} disabled={busy} data-testid="doronpay-vt-execute-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Execute Live
            </Button>
          </div>
        </div>
        {vtRail === 'cyclos' && vtCyclosSource === 'master_to_member' && (
          <p className="text-[10px] text-amber-700 mb-2" data-testid="doronpay-vt-dualcontrol-note">Master → member transfers require dual-control authorization in Cyclos; the DoronPay instruction is only issued after the authorizer approves.</p>
        )}
        {vtResult?.mode === 'pending_authorization' && (
          <div className="flex items-center gap-2 mb-2 p-2 rounded border border-amber-200 bg-amber-50" data-testid="doronpay-vt-pending-banner">
            <p className="text-[11px] text-amber-800 flex-1">Cyclos txn <span className="font-mono">{vtResult.cyclos_transaction_number}</span> awaits authorization. Once approved in Cyclos:</p>
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-800" onClick={() => downloadPdf(`/doronpay/value-token/${vtResult.pending_id}/certificate`, `EzyTransfer_DoronPay_TokenCertificate_${vtResult.reference}.pdf`, 'Token creation certificate downloaded')} disabled={busy} data-testid="doronpay-vt-certificate-btn">
              Token Certificate
            </Button>
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-800" onClick={() => completeValueToken(vtResult.pending_id)} disabled={busy} data-testid="doronpay-vt-complete-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Complete (verify + issue instruction)'}
            </Button>
          </div>
        )}
        {vtResult && (
          <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto max-h-48" data-testid="doronpay-vt-result">{JSON.stringify(vtResult, null, 1)}</pre>
        )}
      </Card>

      <Card className="p-6" data-testid="doronpay-instruction-card">
        <h4 className="font-semibold text-sm mb-1 flex items-center gap-2"><Send className="w-4 h-4 text-indigo-700" /> USD Credit Instruction</h4>
        <p className="text-xs text-slate-500 mb-3">Debit Mint/Cyclos funding leg → signed CREDIT instruction to DoronPay ({st?.beneficiary_reference}). Acceptance (HTTP 202) is an instruction, not settlement — funds become spendable only when DoronPay reports FUNDS_SETTLED.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><Label className="text-[10px]">Amount (USD)</Label><Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="doronpay-amount-input" /></div>
          <div><Label className="text-[10px]">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="doronpay-desc-input" /></div>
          <div>
            <Label className="text-[10px]">Funding rail</Label>
            <select value={form.source_rail} onChange={(e) => setForm({ ...form, source_rail: e.target.value, source_rail_reference: '', _rail_details: null })} className="w-full border rounded px-2 py-2 text-xs" data-testid="doronpay-rail-select">
              <option value="mint">USD Mint</option>
              <option value="cyclos">Cyclos</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Rail txn ref</Label>
            <div className="flex gap-1">
              <Input value={form.source_rail_reference} onChange={(e) => setForm({ ...form, source_rail_reference: e.target.value })} data-testid="doronpay-rail-ref-input" />
              <Button variant="outline" size="sm" className="shrink-0 px-2" onClick={openRailPicker} disabled={railLoading} data-testid="doronpay-rail-pick-btn">
                {railLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Pick'}
              </Button>
            </div>
          </div>
        </div>
        {form._rail_details && (
          <p className="text-[10px] text-emerald-700 mt-1 font-mono" data-testid="doronpay-rail-details">
            Funding leg: {form.source_rail.toUpperCase()} {form.source_rail_reference} · {Number(form._rail_details.amount).toFixed(2)} · {String(form._rail_details.date || '').slice(0, 10)} · {form._rail_details.description || form._rail_details.party || ''}
          </p>
        )}
        {railTxns && (
          <div className="mt-2 border rounded max-h-48 overflow-y-auto" data-testid="doronpay-rail-txn-list">
            {railTxns.length === 0 ? <p className="text-xs text-slate-500 p-2">No transactions found.</p> : railTxns.map((t) => (
              <button key={t.transaction_number} type="button" onClick={() => pickRailTxn(t)}
                className="w-full text-left px-2 py-1.5 text-[11px] font-mono hover:bg-indigo-50 border-b border-slate-100 flex justify-between gap-2"
                data-testid={`doronpay-rail-txn-use-${t.transaction_number}`}>
                <span>{t.transaction_number} · {String(t.date || '').slice(0, 10)} · {t.party || t.description || t.kind}</span>
                <span className={Number(t.amount) < 0 ? 'text-red-600' : 'text-emerald-700'}>{Number(t.amount).toFixed(2)}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-3 flex-wrap">
          <Button variant="outline" onClick={prepare} disabled={busy} data-testid="doronpay-prepare-btn">
            <Eye className="w-4 h-4 mr-1" /> Prepare (show signed request)
          </Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800" onClick={execute} disabled={busy || !prepared} data-testid="doronpay-execute-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Execute LIVE
          </Button>
        </div>
        {prepared && (
          <pre className="mt-3 bg-slate-900 text-emerald-300 text-[10px] p-3 rounded overflow-x-auto max-h-72" data-testid="doronpay-prepared-view">{JSON.stringify(prepared, null, 2)}</pre>
        )}
      </Card>

      <Card className="p-6" data-testid="doronpay-event-card">
        <h4 className="font-semibold text-sm mb-1 flex items-center gap-2"><FileSignature className="w-4 h-4 text-indigo-700" /> Report Settlement Event</h4>
        <p className="text-xs text-slate-500 mb-3">Report EzyTransfer processing state to DoronPay for an existing instruction (COMPLETED requires the funding-leg transaction id).</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><Label className="text-[10px]">External reference</Label><Input value={evt.external_reference} onChange={(e) => setEvt({ ...evt, external_reference: e.target.value })} placeholder="EZY-DORON-USD-…" data-testid="doronpay-evt-ref-input" /></div>
          <div>
            <Label className="text-[10px]">Status</Label>
            <select value={evt.status} onChange={(e) => setEvt({ ...evt, status: e.target.value })} className="w-full border rounded px-2 py-2 text-xs" data-testid="doronpay-evt-status-select">
              <option>SUBMITTED</option><option>COMPLETED</option><option>FAILED</option>
            </select>
          </div>
          <div><Label className="text-[10px]">Transaction ID (COMPLETED)</Label><Input value={evt.transaction_id} onChange={(e) => setEvt({ ...evt, transaction_id: e.target.value })} data-testid="doronpay-evt-txid-input" /></div>
          <div><Label className="text-[10px]">Amount (USD)</Label><Input value={evt.amount} onChange={(e) => setEvt({ ...evt, amount: e.target.value })} data-testid="doronpay-evt-amount-input" /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={() => sendEvent(false)} disabled={busy} data-testid="doronpay-evt-prepare-btn">Prepare</Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800" onClick={() => sendEvent(true)} disabled={busy} data-testid="doronpay-evt-send-btn">Send event</Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="doronpay-ledger-card">
        <h4 className="font-semibold text-sm mb-2">DoronPay Instruction Ledger</h4>
        {rows.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="doronpay-ledger-empty">No DoronPay instructions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-50"><tr><th className="text-left px-2 py-1">Reference</th><th className="text-right px-2 py-1">Amount</th><th className="text-left px-2 py-1">Rail ref</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">Stage</th><th className="text-left px-2 py-1">Spendable</th><th className="text-left px-2 py-1">Redemption</th><th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Act</th></tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`doronpay-row-${s.reference}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{s.reference}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{Number(s.amount).toFixed(2)} USD</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{s.source_rail}{s.source_rail_reference ? `:${s.source_rail_reference}` : ''}</td>
                    <td className="px-2 py-1.5">
                      <Badge className={`text-[10px] border ${s.ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {s.doron_status || (s.ok ? 'ACCEPTED' : `HTTP ${s.http_status}`)}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{s.settlement_stage || '—'}</td>
                    <td className="px-2 py-1.5">{s.spendable ? '✓' : '—'}</td>
                    <td className="px-2 py-1.5" data-testid={`doronpay-redemption-status-${s.reference}`}>
                      {s.recalled && (
                        <Badge className="text-[9px] border bg-rose-50 text-rose-700 border-rose-200" title={`${(s.recall || {}).cyclos_transaction || ''} ${(s.recall || {}).reference || ''}`} data-testid={`doronpay-recalled-badge-${s.reference}`}>RECALLED</Badge>
                      )}
                      {s.redeemed ? (
                        <Badge className="text-[9px] border bg-emerald-50 text-emerald-700 border-emerald-200" title={`${(s.redemption || {}).channel || ''} ${(s.redemption || {}).reference || ''}`}>REDEEMED</Badge>
                      ) : (
                        <Badge className="text-[9px] border bg-slate-50 text-slate-600 border-slate-200">RING-FENCED</Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{String(s.created_at || '').slice(0, 16)}</td>
                    <td className="px-2 py-1.5">
                      <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => pollRow(s)} disabled={busy} title="Query DoronPay status" data-testid={`doronpay-poll-btn-${s.reference}`}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => downloadReceipt(s)} title="Download stamped receipt PDF" data-testid={`doronpay-receipt-btn-${s.reference}`}>
                        <FileDown className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => downloadEvidencePack(s)} title="Download verification evidence pack (cover + funding receipt + settlement receipt)" data-testid={`doronpay-evidence-btn-${s.reference}`}>
                        <FileStack className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => downloadPdf(`/doronpay/instructions/${s.id}/value-delivery-statement`, `EzyTransfer_DoronPay_ValueDelivery_${s.reference}.pdf`, 'Value delivery statement downloaded')} title="Download value delivery statement PDF" data-testid={`doronpay-valuedelivery-btn-${s.reference}`}>
                        <FileText className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => downloadPdf(`/doronpay/instructions/${s.id}/redemption-statement`, `EzyTransfer_DoronPay_Redemption_${s.reference}.pdf`, 'Redemption statement downloaded')} title="Download redemption statement PDF" data-testid={`doronpay-redemption-btn-${s.reference}`}>
                        <Banknote className="w-3 h-3" />
                      </Button>
                      {s.value_token && (
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-indigo-700" onClick={() => downloadPdf(`/doronpay/instructions/${s.id}/dtr`, `EzyTransfer_DoronPay_DTR_${s.reference}.pdf`, 'Digital Token Receipt downloaded')} title="Download Digital Token Receipt (DTR)" data-testid={`doronpay-dtr-btn-${s.reference}`}>
                          <FileSignature className="w-3 h-3" />
                        </Button>
                      )}
                      {!s.redeemed && (
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-emerald-700" onClick={() => markRedeemed(s)} disabled={busy} title="Mark as redeemed (one-way, blocks double redemption)" data-testid={`doronpay-redeem-btn-${s.reference}`}>
                          <BadgeCheck className="w-3 h-3" />
                        </Button>
                      )}
                      {s.redeemed && (
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-emerald-700" onClick={() => downloadPdf(`/doronpay/instructions/${s.id}/redemption-receipt`, `EzyTransfer_DoronPay_RedemptionReceipt_${s.reference}.pdf`, 'Stamped redemption receipt downloaded')} title="Download stamped redemption receipt" data-testid={`doronpay-redemption-receipt-btn-${s.reference}`}>
                          <Stamp className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DoronPayLayer;
